// The role picker after a web dual-role sign-in (FR-031; R-2, R-6): "Continue as parent" or
// "Continue as school admin · <school name>". The choice is remembered for the next restore
// (controller.switchRole) and opens that role's area; school-admin code loads only on that choice.
import { useRouter } from "expo-router";
import { useEffect } from "react";
import { StyleSheet, View } from "react-native";

import { useSession, useSessionController } from "@/session/hooks";
import type { ServedRole } from "@/session/types";
import { makeStyles } from "@/theme/makeStyles";
import { Button } from "@/ui/Button";

import { AUTH_COPY } from "../copy";
import { AuthCard } from "../ui/AuthCard";

const useStyles = makeStyles((t) =>
  StyleSheet.create({ options: { gap: t.space.md } }),
);

export function ChooseRoleScreen() {
  const styles = useStyles();
  const session = useSession();
  const router = useRouter();
  const { switchRole, takePostSignInHref } = useSessionController();

  // This page was the destination chosen at sign-in; it is followed once.
  useEffect(() => {
    takePostSignInHref();
  }, [takePostSignInHref]);

  const schools =
    session.status === "signedIn" ? session.schoolAdminSchools : [];

  const choose = async (role: ServedRole) => {
    await switchRole(role);
    router.replace(role === "SCHOOL_ADMIN" ? "/school" : "/");
  };

  return (
    <AuthCard title={AUTH_COPY.roles.chooseTitle} testID="choose-role-screen">
      <View style={styles.options}>
        <Button onPress={() => void choose("PARENT")} testID="choose-parent">
          {AUTH_COPY.roles.continueAsParent}
        </Button>
        {schools.length > 0 ? (
          <Button
            variant="outline"
            onPress={() => void choose("SCHOOL_ADMIN")}
            testID="choose-school-admin"
          >
            {AUTH_COPY.roles.continueAsSchoolAdmin(
              schools.length === 1 ? schools[0]!.name : null,
            )}
          </Button>
        ) : null}
      </View>
    </AuthCard>
  );
}
